import { useEffect, useRef, useState } from 'react';
import type { TouchEvent } from 'react';
import type { Knowledge, KnowledgeGroup } from '../../types';
import Modal from '@/components/base/Modal';
import { GROUP_COLORS } from '../notes/groupColors';
import { formatDate } from '../notes/noteUtils';
import LinkifiedText from '../LinkifiedText';
import AttachmentList from '../AttachmentList';

interface KnowledgeViewModalProps {
  item: Knowledge | null;
  group?: KnowledgeGroup;
  imageUrls?: string[];
  onClose: () => void;
  onEdit: (item: Knowledge) => void;
}

export default function KnowledgeViewModal({
  item,
  group,
  imageUrls,
  onClose,
  onEdit,
}: KnowledgeViewModalProps) {
  const [zoomIndex, setZoomIndex] = useState<number | null>(null);
  const touchStart = useRef<{ x: number; y: number } | null>(null);

  useEffect(() => {
    setZoomIndex(null);
  }, [item?.id]);

  if (!item) return null;

  const groupStyle = group
    ? GROUP_COLORS[group.color] ?? GROUP_COLORS.primary
    : null;

  const images = imageUrls ?? [];
  const zoomUrl = zoomIndex !== null ? images[zoomIndex] : null;

  const showPrev = () => {
    if (zoomIndex !== null && images.length > 1) {
      setZoomIndex((zoomIndex - 1 + images.length) % images.length);
    }
  };

  const showNext = () => {
    if (zoomIndex !== null && images.length > 1) {
      setZoomIndex((zoomIndex + 1) % images.length);
    }
  };

  const handleTouchStart = (e: TouchEvent) => {
    const touch = e.touches[0];
    touchStart.current = { x: touch.clientX, y: touch.clientY };
  };

  const handleTouchEnd = (e: TouchEvent) => {
    if (!touchStart.current || images.length <= 1) return;
    const touch = e.changedTouches[0];
    const dx = touch.clientX - touchStart.current.x;
    const dy = touch.clientY - touchStart.current.y;
    touchStart.current = null;

    const SWIPE_THRESHOLD = 50;
    const absX = Math.abs(dx);
    const absY = Math.abs(dy);

    // Ignore small/likely-intentional taps or diagonal gestures
    if (Math.max(absX, absY) < SWIPE_THRESHOLD) return;

    if (absX >= absY) {
      // Horizontal swipe: left = next, right = prev
      if (dx < 0) showNext();
      else showPrev();
    } else {
      // Vertical swipe: up = next, down = prev
      if (dy < 0) showNext();
      else showPrev();
    }
  };

  return (
    <>
      <Modal
        open={!!item}
        onClose={onClose}
        title="Chi tiết kiến thức"
        maxWidth="max-w-xl"
        footer={
          <>
            <button
              type="button"
              onClick={onClose}
              className="whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2 text-sm font-medium text-foreground-700 transition hover:bg-background-100"
            >
              Đóng
            </button>
            <button
              type="button"
              onClick={() => onEdit(item)}
              className="flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50 transition hover:bg-primary-600"
            >
              <i className="ri-edit-line text-sm" />
              Chỉnh sửa
            </button>
          </>
        }
      >
        <div className="space-y-4">
          <h4 className="font-heading text-lg font-semibold leading-snug text-foreground-950">
            {item.title || 'Không có tiêu đề'}
          </h4>

          <div className="flex flex-wrap items-center gap-1.5">
            {group && groupStyle && (
              <span
                className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${groupStyle.badge}`}
              >
                <span className={`h-1.5 w-1.5 rounded-full ${groupStyle.dot}`} />
                {group.name}
              </span>
            )}
          </div>

          <div className="rounded-lg border border-background-200 bg-background-100/50 p-4">
            <p className="whitespace-pre-wrap text-sm leading-relaxed text-foreground-700">
              {item.content ? (
                <LinkifiedText text={item.content} />
              ) : (
                <span className="italic text-foreground-400">
                  Không có nội dung
                </span>
              )}
            </p>
          </div>

          {images.length > 0 && (
            <div className="grid grid-cols-3 gap-2">
              {images.map((url, index) => (
                <button
                  key={url}
                  type="button"
                  onClick={() => setZoomIndex(index)}
                  className="group relative aspect-square overflow-hidden rounded-lg border border-background-200"
                  aria-label="Phóng to ảnh"
                >
                  <img
                    src={url}
                    alt={`${item.title || 'Hình minh họa'} ${index + 1}`}
                    className="h-full w-full cursor-zoom-in object-cover transition group-hover:opacity-90"
                  />
                  <span className="absolute bottom-1 right-1 flex h-6 w-6 items-center justify-center rounded-md bg-black/50 text-white opacity-0 transition group-hover:opacity-100">
                    <i className="ri-zoom-in-line text-sm" />
                  </span>
                </button>
              ))}
            </div>
          )}

          <AttachmentList
            attachments={item.attachments ?? []}
            bucket="noteapp_attachments"
          />

          <div className="space-y-2 rounded-lg border border-background-200 bg-background-50 p-4 text-sm">
            <div className="flex items-center justify-between gap-3">
              <span className="text-foreground-500">Ngày tạo</span>
              <span className="font-medium text-foreground-700">
                {formatDate(item.created_at)}
              </span>
            </div>
            <div className="flex items-center justify-between gap-3">
              <span className="text-foreground-500">Cập nhật lần cuối</span>
              <span className="font-medium text-foreground-700">
                {formatDate(item.updated_at)}
              </span>
            </div>
          </div>
        </div>
      </Modal>

      {zoomUrl && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 p-4"
          onClick={() => setZoomIndex(null)}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          <button
            type="button"
            onClick={() => setZoomIndex(null)}
            className="absolute right-4 top-4 flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white transition hover:bg-white/20"
            aria-label="Đóng"
          >
            <i className="ri-close-line text-xl" />
          </button>

          {images.length > 1 && (
            <>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  showPrev();
                }}
                className="absolute left-4 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-white transition hover:bg-white/20"
                aria-label="Ảnh trước"
              >
                <i className="ri-arrow-left-line text-xl" />
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  showNext();
                }}
                className="absolute right-4 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-white transition hover:bg-white/20"
                aria-label="Ảnh sau"
              >
                <i className="ri-arrow-right-line text-xl" />
              </button>
            </>
          )}

          <img
            src={zoomUrl}
            alt={item.title || 'Hình minh họa'}
            className="max-h-full max-w-full rounded-lg object-contain"
            onClick={(e) => e.stopPropagation()}
          />

          {images.length > 1 && zoomIndex !== null && (
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 rounded-full bg-white/10 px-3 py-1 text-sm text-white">
              {zoomIndex + 1} / {images.length}
            </div>
          )}
        </div>
      )}
    </>
  );
}